import { Trans, useLingui } from "@lingui/react/macro";
import { ChatMarkdown } from "@rakazo/chat-ui/web";
import type { Bot, Ticket, TicketPriority, TicketStatus } from "@rakazo/contracts";
import {
  TICKET_COMMENT_MAX_LENGTH,
  TICKET_DESCRIPTION_MAX_LENGTH,
  TICKET_PRIORITIES,
  TICKET_STATUSES,
  TICKET_TITLE_MAX_LENGTH,
} from "@rakazo/contracts";
import { isRetryableError, retryDelayMs } from "@rakazo/core";
import { fetchFresh, writeSaved } from "@rakazo/core/query";
import {
  BotAvatar,
  Button,
  buttonVariants,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Input,
  NativeSelect,
  NativeSelectOption,
  Textarea,
} from "@rakazo/ui-web";
import { skipToken, useQuery, useQueryClient } from "@tanstack/react-query";
import { ChevronDown, ChevronLeft, ChevronRight } from "lucide-react";
import type { ReactNode } from "react";
import { useEffect, useId, useState } from "react";
import { Link } from "react-router-dom";
import { spaceQueries, useQueryUserId, useSpaceQueries } from "../lib/query";
import { formatRelativeTime } from "../lib/relative-time";
import { rpc, rpcForSpace } from "../lib/rpc";
import { useTicketPriorityLabels } from "../lib/ticket-priority";
import { useTicketStatusLabels } from "../lib/ticket-status";
import { errorText } from "../lib/user-error";
import { WindowChrome } from "./WindowChrome";

export function BoardPage() {
  const { t } = useLingui();
  const { tickets, bots, error, reload } = useTicketBoard();
  const [selected, setSelected] = useState<Ticket | "new" | null>(null);

  return (
    <div className="flex h-full min-w-0 flex-col overflow-hidden bg-background text-foreground">
      <WindowChrome />
      <header className="flex items-center gap-3 border-b px-4 py-3">
        <Link
          to="/app"
          className={buttonVariants({ variant: "ghost", size: "icon" })}
          aria-label={t`Back`}
        >
          <ChevronLeft />
        </Link>
        <h1 className="flex-1 font-medium">
          <Trans>Board</Trans>
        </h1>
        <Button onClick={() => setSelected("new")} disabled={bots.length === 0}>
          <Trans>New ticket</Trans>
        </Button>
      </header>
      {error ? (
        <p role="alert" className="px-4 py-2 text-sm text-destructive">
          {error}
        </p>
      ) : null}
      <main className="relative flex min-h-0 min-w-0 flex-1 overflow-hidden p-4">
        <TicketBoardColumns tickets={tickets} bots={bots} error={error} onSelect={setSelected} />
      </main>
      {selected ? (
        <TicketDialog
          key={selected === "new" ? "new" : selected.id}
          ticket={selected === "new" ? null : selected}
          bots={bots}
          onClose={() => setSelected(null)}
          onSaved={reload}
        />
      ) : null}
    </div>
  );
}

// BOORO: the board's data, columns and dialog are exported so the Tasks area can embed them.
export function useTicketBoard() {
  const queries = useSpaceQueries();
  const queryClient = useQueryClient();
  const ticketsOptions = queries.tickets.list.queryOptions({ input: {} });
  const ticketsQuery = useQuery(ticketsOptions);
  const botsQuery = useQuery(queries.bots.list.queryOptions());
  const tickets = ticketsQuery.data?.tickets ?? [];
  const bots = botsQuery.data ?? [];
  const [streamError, setError] = useState("");
  const loadFailed = ticketsQuery.error ?? botsQuery.error;
  const error = streamError || (loadFailed ? errorText(loadFailed) : "");
  /** A read sent after a change, so an older one in flight cannot bring back the old board. */
  const reload = () => fetchFresh(queryClient, ticketsOptions).then(() => undefined);

  useEffect(() => {
    let currentController: AbortController | undefined;
    let active = true;
    // Live events refresh the board and any open ticket's comments.
    const refreshComments = (ticketId?: string) =>
      void queryClient.invalidateQueries({
        queryKey: ticketId
          ? queries.tickets.comments.queryKey({ input: { ticketId } })
          : queries.tickets.comments.key(),
      });
    let cancelRetry: (() => void) | undefined;
    void (async () => {
      while (active) {
        const controller = new AbortController();
        currentController = controller;
        let failure: unknown;
        try {
          const stream = await rpc.boards.subscribe(undefined, {
            signal: controller.signal,
          });
          if (!active) return;
          await fetchFresh(queryClient, ticketsOptions);
          if (!active) return;
          refreshComments();
          setError("");
          for await (const event of stream) {
            if (!active) return;
            refreshComments(event.ticketId);
            await fetchFresh(queryClient, ticketsOptions);
          }
        } catch (cause) {
          failure = cause;
          if (active) setError(errorText(cause));
        } finally {
          controller.abort();
        }
        if (!active || !isRetryableError(failure)) return;
        await new Promise<void>((resolve) => {
          const timer = setTimeout(resolve, retryDelayMs(failure, 1_000));
          cancelRetry = () => {
            clearTimeout(timer);
            resolve();
          };
        });
        cancelRetry = undefined;
      }
    })();
    return () => {
      active = false;
      currentController?.abort();
      cancelRetry?.();
    };
  }, [queries]);

  return { tickets, bots, error, reload };
}

// BOORO: the board shows agents of several offices; it needs only their face and name.
export type BoardAgent = Pick<Bot, "id" | "name" | "color">;

const PRIORITY_RANK: Record<TicketPriority, number> = {
  low: 0,
  normal: 1,
  high: 2,
  urgent: 3,
};

function ticketTime(value: string | null | undefined): number {
  const time = value ? Date.parse(value) : Number.NaN;
  return Number.isFinite(time) ? time : 0;
}

/**
 * The order inside a column. Todo shows the most important and oldest work
 * first; work in progress shows the latest change; waiting work shows what has
 * waited longest. Finished work shows the newest result first.
 */
export function sortTicketsForColumn(status: TicketStatus, tickets: readonly Ticket[]): Ticket[] {
  return [...tickets].sort((a, b) => {
    const priority = PRIORITY_RANK[b.priority] - PRIORITY_RANK[a.priority];
    if (status === "done" || status === "closed") {
      const changed =
        ticketTime(b.completedAt ?? b.updatedAt) - ticketTime(a.completedAt ?? a.updatedAt);
      if (changed !== 0) return changed;
    } else if (status === "todo") {
      if (priority !== 0) return priority;
      const created = ticketTime(a.createdAt) - ticketTime(b.createdAt);
      if (created !== 0) return created;
    } else if (status === "doing") {
      const changed = ticketTime(b.updatedAt) - ticketTime(a.updatedAt);
      if (changed !== 0) return changed;
    } else {
      if (priority !== 0) return priority;
      const changed = ticketTime(a.updatedAt) - ticketTime(b.updatedAt);
      if (changed !== 0) return changed;
    }
    return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
  });
}

export function TicketBoardColumns({
  tickets,
  bots,
  error,
  onSelect,
  cardExtra,
  cardBotStatus,
  cardAction,
  cardMenu,
  emptyLabel,
  cardLimit = { done: 15, closed: 10 },
  defaultCollapsed = ["closed"],
}: {
  tickets: Ticket[];
  bots: readonly BoardAgent[];
  error: string;
  onSelect: (ticket: Ticket) => void;
  // BOORO: the task's result under its card.
  cardExtra?: (ticket: Ticket) => ReactNode;
  // BOORO: the status of the run that works on this task, for the avatar.
  cardBotStatus?: (ticket: Ticket) => string | undefined;
  // BOORO: an action on the card itself, such as stopping the agent.
  cardAction?: (ticket: Ticket) => ReactNode;
  // BOORO: the task menu with status changes and other actions.
  cardMenu?: (ticket: Ticket) => ReactNode;
  // BOORO: the empty state may explain that filters hide the tasks.
  emptyLabel?: ReactNode;
  // BOORO: finished work stays short until the person asks for the rest.
  cardLimit?: Partial<Record<TicketStatus, number>>;
  // BOORO: declined work is noise by default, but remains one click away.
  defaultCollapsed?: readonly TicketStatus[];
}) {
  const { t } = useLingui();
  const statuses = useTicketStatusLabels();
  const priorities = useTicketPriorityLabels();
  const [collapsed, setCollapsed] = useState<Partial<Record<TicketStatus, boolean>>>(() => {
    const initial: Partial<Record<TicketStatus, boolean>> = {};
    for (const status of defaultCollapsed) initial[status] = true;
    return initial;
  });
  const [expanded, setExpanded] = useState<Partial<Record<TicketStatus, boolean>>>({});

  return (
    <div className="relative h-full min-h-0 w-full">
      <div data-testid="board-columns" className="flex h-full min-h-0 gap-3 overflow-x-auto pb-1">
        {TICKET_STATUSES.map((status) => {
          const rows = sortTicketsForColumn(
            status,
            tickets.filter((ticket) => ticket.status === status),
          );
          const isCollapsed = collapsed[status] ?? false;
          const limit = cardLimit[status] ?? rows.length;
          const shown = expanded[status] ? rows : rows.slice(0, limit);
          const hidden = rows.length - shown.length;
          const collapseLabel = `${isCollapsed ? t`Expand` : t`Collapse`} ${statuses[status]}`;
          return (
            // BOORO: the columns fit next to the sidebar, so the board does not
            // grow sideways. Narrow screens scroll the board itself, while each
            // column keeps its own vertical scroll.
            <section
              key={status}
              data-testid={`board-column-${status}`}
              className="flex h-full min-h-0 min-w-36 flex-1 basis-36 flex-col"
            >
              <div className="mb-1 flex items-center gap-1 px-1">
                <button
                  type="button"
                  aria-label={collapseLabel}
                  aria-expanded={!isCollapsed}
                  className="rounded p-0.5 text-muted-foreground hover:bg-accent hover:text-foreground"
                  onClick={() =>
                    setCollapsed((previous) => ({ ...previous, [status]: !isCollapsed }))
                  }
                >
                  {isCollapsed ? (
                    <ChevronRight className="size-3.5" />
                  ) : (
                    <ChevronDown className="size-3.5" />
                  )}
                </button>
                <h2 className="min-w-0 flex-1 truncate text-sm font-medium">{statuses[status]}</h2>
                <span
                  data-testid={`board-count-${status}`}
                  className="rounded-full bg-muted px-1.5 py-0.5 text-[11px] tabular-nums text-muted-foreground"
                >
                  {rows.length}
                </span>
              </div>
              {isCollapsed ? null : (
                <div className="rk-scroll mt-1 min-h-0 flex-1 space-y-2 overflow-y-auto pb-2 pe-1">
                  {shown.map((ticket) => {
                    const bot = bots.find((candidate) => candidate.id === ticket.assigneeBotId);
                    const action = cardAction?.(ticket);
                    const menu = cardMenu?.(ticket);
                    return (
                      <div key={ticket.id} className="relative">
                        <Button
                          data-testid={`board-card-${ticket.id}`}
                          data-status={ticket.status}
                          variant="outline"
                          className="h-auto w-full flex-col items-start gap-1 whitespace-normal p-3 text-left font-normal"
                          onClick={() => onSelect(ticket)}
                        >
                          <span
                            className={`flex w-full items-center justify-between gap-2 text-xs text-muted-foreground ${
                              action || menu ? "pe-7" : ""
                            }`}
                          >
                            <span>{ticket.ref}</span>
                            {ticket.priority !== "normal" ? (
                              <span
                                className={`shrink-0 font-medium ${
                                  ticket.priority === "urgent"
                                    ? "text-destructive"
                                    : ticket.priority === "high"
                                      ? "text-warning"
                                      : "text-muted-foreground"
                                }`}
                              >
                                {priorities[ticket.priority]}
                              </span>
                            ) : null}
                          </span>
                          {/* BOORO: a long title does not make the card tall. */}
                          <span className="line-clamp-3">{ticket.title}</span>
                          {/* BOORO: the agent with its avatar, alive while it works on the task. */}
                          <span className="flex w-full items-center justify-between gap-2 text-xs text-muted-foreground">
                            <span className="flex min-w-0 items-center gap-1.5">
                              {bot ? (
                                <BotAvatar
                                  color={bot.color}
                                  identity={bot.id}
                                  size={18}
                                  status={cardBotStatus?.(ticket)}
                                />
                              ) : null}
                              {bot?.name ? <span className="truncate">{bot.name}</span> : null}
                            </span>
                            <time dateTime={ticket.updatedAt} className="shrink-0">
                              {formatRelativeTime(ticket.updatedAt)}
                            </time>
                          </span>
                          {cardExtra?.(ticket)}
                        </Button>
                        {action || menu ? (
                          <div className="absolute end-1 top-1 flex items-center gap-1">
                            {action}
                            {menu}
                          </div>
                        ) : null}
                      </div>
                    );
                  })}
                  {hidden > 0 ? (
                    <Button
                      data-testid={`board-show-all-${status}`}
                      variant="ghost"
                      size="sm"
                      className="w-full text-xs text-muted-foreground"
                      onClick={() => setExpanded((previous) => ({ ...previous, [status]: true }))}
                    >
                      <Trans>Show all</Trans> {rows.length}
                    </Button>
                  ) : null}
                </div>
              )}
            </section>
          );
        })}
      </div>
      {tickets.length === 0 && !error ? (
        <p className="pointer-events-none absolute inset-0 flex items-center justify-center text-sm text-muted-foreground">
          {emptyLabel ?? <Trans>No tickets yet</Trans>}
        </p>
      ) : null}
    </div>
  );
}

export function TicketDialog({
  ticket,
  bots,
  onClose,
  onSaved,
  extra,
  spaceId,
}: {
  // BOORO: task details shown above the form.
  extra?: ReactNode;
  // BOORO: the office of the task, which need not be the selected one.
  spaceId?: string;
  ticket: Ticket | null;
  bots: readonly BoardAgent[];
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const { t } = useLingui();
  const formId = useId();
  const statuses = useTicketStatusLabels();
  const priorities = useTicketPriorityLabels();
  const [title, setTitle] = useState(ticket?.title ?? "");
  const [description, setDescription] = useState(ticket?.description ?? "");
  const [owner, setOwner] = useState(ticket?.assigneeBotId ?? bots[0]?.id ?? "");
  const [status, setStatus] = useState<TicketStatus>(ticket?.status ?? "todo");
  const [priority, setPriority] = useState<TicketPriority>(ticket?.priority ?? "normal");
  const userId = useQueryUserId();
  const selectedQueries = useSpaceQueries();
  const queries = spaceId ? spaceQueries(userId, spaceId) : selectedQueries;
  const client = spaceId ? rpcForSpace(spaceId) : rpc;
  const queryClient = useQueryClient();
  const commentsOptions = queries.tickets.comments.queryOptions({
    input: ticket ? { ticketId: ticket.id } : skipToken,
  });
  const commentsQuery = useQuery(commentsOptions);
  const comments = commentsQuery.data ?? [];
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const shownError = error || (commentsQuery.error ? errorText(commentsQuery.error) : "");

  async function save() {
    setBusy(true);
    setError("");
    try {
      if (ticket)
        await client.tickets.update({
          id: ticket.id,
          ...(title !== ticket.title ? { title } : {}),
          ...(description !== (ticket.description ?? "") ? { description } : {}),
          ...(owner !== (ticket.assigneeBotId ?? bots[0]?.id ?? "")
            ? { assigneeBotId: owner }
            : {}),
          ...(status !== ticket.status ? { status } : {}),
          ...(priority !== ticket.priority ? { priority } : {}),
        });
      else await client.tickets.create({ title, description, assigneeBotId: owner, priority });
      await onSaved();
      onClose();
    } catch (cause) {
      setError(errorText(cause));
    } finally {
      setBusy(false);
    }
  }
  async function comment() {
    if (!ticket) return;
    setBusy(true);
    setError("");
    try {
      const row = await client.tickets.comment({ ticketId: ticket.id, body });
      const options = queries.tickets.comments.queryOptions({ input: { ticketId: ticket.id } });
      // A list already on screen shows the comment at once, unless a read already brought it.
      if (queryClient.getQueryData(options.queryKey)) {
        await writeSaved(queryClient, options.queryKey, (previous = []) =>
          previous.some((item) => item.id === row.id) ? previous : [...previous, row],
        );
      }
      setBody("");
      // Saving cancelled any read in flight, so the whole list is read again after the post.
      void fetchFresh(queryClient, options).catch(() => undefined);
    } catch (cause) {
      setError(errorText(cause));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      {/* BOORO: the detail of a task needs room; it must not scroll sideways. */}
      <DialogContent className="max-h-[90vh] overflow-y-auto overflow-x-hidden sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>{ticket?.title ?? t`New ticket`}</DialogTitle>
        </DialogHeader>
        {extra}
        <form
          // BOORO: the wide dialog puts the fields next to each other, so the
          // detail of a task fits on one screen.
          className="grid gap-3 sm:grid-cols-2"
          onSubmit={(event) => {
            event.preventDefault();
            void save();
          }}
        >
          <label htmlFor={`${formId}-title`} className="block space-y-1 text-sm sm:col-span-2">
            <span>
              <Trans>Title</Trans>
            </span>
            <Input
              id={`${formId}-title`}
              value={title}
              onChange={(event) => setTitle(event.target.value)}
              maxLength={TICKET_TITLE_MAX_LENGTH}
              required
            />
          </label>
          <label
            htmlFor={`${formId}-description`}
            className="block space-y-1 text-sm sm:col-span-2"
          >
            <span>
              <Trans>Description</Trans>
            </span>
            <Textarea
              id={`${formId}-description`}
              data-testid="ticket-description"
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              maxLength={TICKET_DESCRIPTION_MAX_LENGTH}
            />
          </label>
          <label htmlFor={`${formId}-owner`} className="block space-y-1 text-sm">
            <span>
              <Trans>Owner</Trans>
            </span>
            <NativeSelect
              className="w-full"
              id={`${formId}-owner`}
              value={owner}
              onChange={(event) => setOwner(event.target.value)}
              required
            >
              {bots.map((bot) => (
                <NativeSelectOption key={bot.id} value={bot.id}>
                  {bot.name}
                </NativeSelectOption>
              ))}
            </NativeSelect>
          </label>
          {ticket ? (
            <label htmlFor={`${formId}-status`} className="block space-y-1 text-sm">
              <span>
                <Trans>Status</Trans>
              </span>
              <NativeSelect
                className="w-full"
                id={`${formId}-status`}
                value={status}
                onChange={(event) => setStatus(event.target.value as TicketStatus)}
              >
                {TICKET_STATUSES.map((value) => (
                  <NativeSelectOption key={value} value={value}>
                    {statuses[value]}
                  </NativeSelectOption>
                ))}
              </NativeSelect>
            </label>
          ) : null}
          <label htmlFor={`${formId}-priority`} className="block space-y-1 text-sm">
            <span>
              <Trans>Priority</Trans>
            </span>
            <NativeSelect
              className="w-full"
              id={`${formId}-priority`}
              value={priority}
              onChange={(event) => setPriority(event.target.value as TicketPriority)}
            >
              {TICKET_PRIORITIES.map((value) => (
                <NativeSelectOption key={value} value={value}>
                  {priorities[value]}
                </NativeSelectOption>
              ))}
            </NativeSelect>
          </label>
          <div className="flex justify-end sm:col-span-2">
            <Button type="submit" disabled={busy || !title.trim() || !owner}>
              {ticket ? t`Save` : t`Create`}
            </Button>
          </div>
        </form>
        {ticket ? (
          <div data-testid="ticket-comments" className="space-y-3 border-t pt-3">
            {comments.map((row) => (
              <div key={row.id} className="text-sm">
                <div className="mb-1 flex items-center justify-between gap-3 text-xs text-muted-foreground">
                  <span>{row.authorName}</span>
                  <time dateTime={row.createdAt}>{formatRelativeTime(row.createdAt)}</time>
                </div>
                <ChatMarkdown>{row.body}</ChatMarkdown>
              </div>
            ))}
            <form
              className="space-y-2"
              onSubmit={(event) => {
                event.preventDefault();
                void comment();
              }}
            >
              {/* BOORO: the field says what it is for. */}
              <label className="block space-y-1 text-sm" htmlFor={`${formId}-comment`}>
                <span>
                  <Trans>Comment</Trans>
                </span>
                <Textarea
                  id={`${formId}-comment`}
                  aria-label={t`Comment`}
                  value={body}
                  onChange={(event) => setBody(event.target.value)}
                  maxLength={TICKET_COMMENT_MAX_LENGTH}
                />
              </label>
              <div className="flex justify-end">
                <Button type="submit" disabled={busy || !body.trim()}>
                  <Trans>Comment</Trans>
                </Button>
              </div>
            </form>
          </div>
        ) : null}
        {shownError ? (
          <p role="alert" className="text-sm text-destructive">
            {shownError}
          </p>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
